import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Rating,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ResolveIcon,
  Cancel as UnresolveIcon,
  Feedback as FeedbackIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { feedbackService, authService } from '../utils/api';
import FeedbackForm from './FeedbackForm';
import FeedbackCard from './FeedbackCard';

const FEEDBACK_TYPE_COLORS = {
  general: 'default',
  quality: 'primary',
  accuracy: 'secondary',
  improvement: 'warning'
};

const FEEDBACK_TYPE_LABELS = {
  general: 'General',
  quality: 'Quality',
  accuracy: 'Accuracy',
  improvement: 'Improvement'
};

const FeedbackList = ({ callId, onFeedbackCountChange }) => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackToEdit, setFeedbackToEdit] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  
  const userRole = authService.getUserRole();
  const isSuperAdmin = userRole === 'super_admin';

  useEffect(() => {
    loadFeedback();
  }, [callId, page]);

  useEffect(() => {
    if (onFeedbackCountChange) {
      onFeedbackCountChange(totalCount);
    }
  }, [totalCount, onFeedbackCountChange]);

  const loadFeedback = async () => {
    if (!callId) return;
    
    setLoading(true);
    try {
      const response = await feedbackService.getCallFeedback(callId, page, 10);
      setFeedback(response.feedback || []);
      setTotalPages(response.total_pages || 1);
      setTotalCount(response.total_count || 0);
    } catch (error) {
      // Silently fail if no organization is selected (direct URL access)
      console.log('Failed to load feedback:', error.message);
      setFeedback([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event, feedbackItem) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedFeedback(feedbackItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFeedback(null);
  };

  const handleEditFeedback = () => {
    setFeedbackToEdit(selectedFeedback);
    setShowFeedbackForm(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setFeedbackToDelete(selectedFeedback);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!feedbackToDelete) return;

    try {
      await feedbackService.deleteFeedback(feedbackToDelete.id);
      toast.success('Feedback deleted successfully!');
      loadFeedback();
    } catch (error) {
      toast.error(error.message || 'Failed to delete feedback');
    } finally {
      setDeleteDialogOpen(false);
      setFeedbackToDelete(null);
    }
  };

  const handleResolveToggle = async (feedbackItem) => {
    try {
      const newResolvedState = !feedbackItem.is_resolved;
      await feedbackService.resolveFeedback(feedbackItem.id, newResolvedState);
      toast.success(`Feedback ${newResolvedState ? 'resolved' : 'unresolved'} successfully!`);
      loadFeedback();
    } catch (error) {
      toast.error(error.message || 'Failed to update feedback status');
    }
    handleMenuClose();
  };

  const handleFeedbackSubmitted = () => {
    setFeedbackToEdit(null);
    loadFeedback();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const canEditOrDelete = (feedbackItem) => {
    // Users can only edit/delete their own feedback
    return feedbackItem.user_id === parseInt(localStorage.getItem('userId')) || isSuperAdmin;
  };

  if (loading && feedback.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FeedbackIcon />
          Call Feedback ({totalCount})
        </Typography>
        <Button
          variant="contained"
          onClick={() => setShowFeedbackForm(true)}
          size="small"
        >
          Add Feedback
        </Button>
      </Box>

      {/* Feedback List */}
      {feedback.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No feedback yet. Be the first to add feedback for this call!
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {feedback.map((item) => (
            <FeedbackCard
              key={item.id}
              feedback={item}
              onFeedbackUpdated={(updatedFeedback) => {
                setFeedback(prev => prev.map(f => f.id === updatedFeedback.id ? updatedFeedback : f));
              }}
              onFeedbackDeleted={(feedbackId) => {
                setFeedback(prev => prev.filter(f => f.id !== feedbackId));
                setTotalCount(prev => prev - 1);
              }}
              compact={true}
            />
          ))}
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedFeedback && canEditOrDelete(selectedFeedback) && [
          <MenuItem key="edit" onClick={handleEditFeedback}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>,
          <MenuItem key="delete" onClick={handleDeleteClick}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        ]}
        
        {selectedFeedback && isSuperAdmin && (
          <MenuItem onClick={() => handleResolveToggle(selectedFeedback)}>
            {selectedFeedback.is_resolved ? (
              <>
                <UnresolveIcon sx={{ mr: 1 }} fontSize="small" />
                Mark as Unresolved
              </>
            ) : (
              <>
                <ResolveIcon sx={{ mr: 1 }} fontSize="small" />
                Mark as Resolved
              </>
            )}
          </MenuItem>
        )}
      </Menu>

      {/* Feedback Form Dialog */}
      <FeedbackForm
        open={showFeedbackForm}
        onClose={() => {
          setShowFeedbackForm(false);
          setFeedbackToEdit(null);
        }}
        callId={callId}
        feedbackToEdit={feedbackToEdit}
        onFeedbackSubmitted={handleFeedbackSubmitted}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Feedback</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this feedback? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedbackList; 